import React from "react";
import {
  Modal,
  View,
  Text,
  Pressable,
  StyleSheet,
  Platform,
  TouchableOpacity,
} from "react-native";
import Icon from "./icon";
import { FontFamily } from "../theme/typography";
import { useTheme } from "../context/themeContext";
import { useResponsive } from "../lib/responsive";

/* ================= PROPS ================= */

type DialogAction = Readonly<{
  label: string;
  onPress: () => void;
}>;

/**
 * SuccessDialog — a reusable "done ✓" confirmation shown AFTER an action
 * completes (the positive counterpart to ConfirmDialog, which asks before).
 * Ported verbatim from SK-Catalog (success icon mapped to Upsell's "check").
 */
type SuccessDialogProps = Readonly<{
  visible: boolean;            // Controls visibility
  title?: string;             // Primary heading
  message?: string;           // Supporting line under the title
  summary?: string[];         // Optional bullet lines (e.g. "6 fields added")
  tone?: "success" | "warning"; // success (green) | warning (amber, partial result)
  primaryAction?: DialogAction;   // Emphasised action (right)
  secondaryAction?: DialogAction; // Secondary action (left)
  onClose?: () => void;       // Top-right close icon + backdrop dismiss
}>;

/* ================= COMPONENT ================= */

export default function SuccessDialog({
  visible,
  title = "All done",
  message,
  summary,
  tone = "success",
  primaryAction,
  secondaryAction,
  onClose,
}: SuccessDialogProps) {
  const { theme } = useTheme();
  const { colors } = theme;
  const { width } = useResponsive();

  const TONES = {
    success: { accent: colors.success, soft: colors.successSoft, icon: "check" as const },
    warning: { accent: colors.warn, soft: colors.warnSoft, icon: "alert-circle" as const },
  };
  const t = TONES[tone];

  return (
    <Modal
      transparent
      visible={visible}
      animationType="fade"
      onRequestClose={onClose}
    >
      <Pressable style={styles.overlay} onPress={onClose}>
        {/* Stop propagation so taps inside the card don't dismiss it. */}
        <Pressable style={[styles.box, { width: Math.min(width * 0.85, 360), backgroundColor: colors.card }]} onPress={() => {}}>
          {/* Accent strip echoes the tone (green = success, amber = partial). */}
          <View style={[styles.indicator, { backgroundColor: t.accent }]} />

          {onClose && (
            <TouchableOpacity
              style={[styles.closeButton, { backgroundColor: colors.paper2 }]}
              onPress={onClose}
              hitSlop={{ top: 20, bottom: 20, left: 20, right: 20 }}
            >
              <Icon name="x" size={22} color={colors.ink4} />
            </TouchableOpacity>
          )}

          <View style={styles.content}>
            {/* Status badge */}
            <View style={[styles.badge, { backgroundColor: t.soft }]}>
              <Icon name={t.icon} size={30} color={t.accent} />
            </View>

            <Text style={[styles.title, { color: colors.ink }]}>{title}</Text>
            {!!message && <Text style={[styles.message, { color: colors.ink3 }]}>{message}</Text>}

            {!!summary?.length && (
              <View style={[styles.summary, { backgroundColor: colors.paper2 }]}>
                {summary.map((line, i) => (
                  <View key={i} style={styles.summaryRow}>
                    <Icon name="check" size={14} color={t.accent} />
                    <Text style={[styles.summaryText, { color: colors.ink2 }]}>{line}</Text>
                  </View>
                ))}
              </View>
            )}
          </View>

          {(primaryAction || secondaryAction) && (
            <View style={[styles.footer, { backgroundColor: colors.paper2 }]}>
              {secondaryAction && (
                <Pressable
                  style={({ pressed }) => [styles.btn, { backgroundColor: colors.paper3 }, pressed && styles.btnPressed]}
                  onPress={secondaryAction.onPress}
                >
                  <Text style={[styles.secondaryText, { color: colors.ink2 }]}>{secondaryAction.label}</Text>
                </Pressable>
              )}
              {primaryAction && (
                <Pressable
                  style={({ pressed }) => [styles.btn, { backgroundColor: t.accent }, pressed && styles.btnPressed]}
                  onPress={primaryAction.onPress}
                >
                  <Text style={[styles.primaryText, { color: colors.white }]}>{primaryAction.label}</Text>
                </Pressable>
              )}
            </View>
          )}
        </Pressable>
      </Pressable>
    </Modal>
  );
}

/* ================= STYLES ================= */

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(15, 23, 42, 0.75)",
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 24,
  },
  box: {
    borderRadius: 20,
    overflow: "hidden",
    ...Platform.select({
      ios: {
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.18,
        shadowRadius: 16,
      },
      android: { elevation: 10 },
      web: { boxShadow: "0 8px 16px rgba(0,0,0,0.18)" } as object,
    }),
  },
  indicator: {
    height: 4,
    width: "100%",
    opacity: 0.9,
  },
  content: {
    paddingTop: 24,
    paddingHorizontal: 20,
    paddingBottom: 18,
    alignItems: "center",
  },
  badge: {
    width: 60,
    height: 60,
    borderRadius: 30,
    justifyContent: "center",
    alignItems: "center",
    marginBottom: 14,
  },
  title: {
    fontSize: 18,
    fontWeight: "800",
    fontFamily: FontFamily.sans,
    textAlign: "center",
    marginBottom: 6,
    letterSpacing: -0.3,
  },
  message: {
    fontSize: 13.5,
    fontFamily: FontFamily.sans,
    textAlign: "center",
    lineHeight: 20,
  },
  summary: {
    alignSelf: "stretch",
    marginTop: 16,
    gap: 8,
    borderRadius: 12,
    padding: 12,
  },
  summaryRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  summaryText: {
    fontSize: 13,
    fontFamily: FontFamily.sans,
    flex: 1,
  },
  footer: {
    flexDirection: "row",
    padding: 14,
    gap: 10,
  },
  btn: {
    flex: 1,
    height: 44,
    borderRadius: 12,
    justifyContent: "center",
    alignItems: "center",
  },
  btnPressed: {
    opacity: 0.85,
    transform: [{ scale: 0.98 }],
  },
  secondaryText: {
    fontSize: 14,
    fontWeight: "700",
    fontFamily: FontFamily.sans,
  },
  primaryText: {
    fontSize: 14,
    fontWeight: "700",
    fontFamily: FontFamily.sans,
  },
  closeButton: {
    position: "absolute",
    top: 14,
    right: 14,
    zIndex: 10,
    borderRadius: 16,
    width: 30,
    height: 30,
    justifyContent: "center",
    alignItems: "center",
  },
});
